import React, { useEffect, useState } from "react";
import {
  Box, Table, Thead, Tbody, Tr, Th, Td, Text, Spinner, Flex, Badge,
} from "@chakra-ui/react";
import { FaCheckCircle, FaTimesCircle, FaMinus } from "react-icons/fa";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;
const RETAILERS = ["pnp", "chk", "dis", "cli", "med", "pet"];
const RETAILER_LABELS = { pnp: "PNP", chk: "CHK", dis: "DIS", cli: "CLI", med: "MED", pet: "PET" };
const SHEET_LABELS = { csv1: "Dashboard", csv2: "Store SOH", csv3: "Top Stores", csv4: "Top SKUs" };

const CsvValidator = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCell, setExpandedCell] = useState(null); // { supplier, retailer }
  const [detailsCache, setDetailsCache] = useState({});
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${API}/validation/csv-validation-summary`, { csv_name: "sales_tracker" });
      if (res.data?.status === 1) {
        setRows(res.data.data);
        if (res.data.data.length > 0) {
          setUpdatedAt(res.data.data[0].updated_at);
        }
      }
    } catch (err) {
      console.error("Failed to fetch validation summary", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = async (supplier, retailer) => {
    const key = `${supplier}|${retailer}`;

    // Toggle off if already expanded
    if (expandedCell?.supplier === supplier && expandedCell?.retailer === retailer) {
      setExpandedCell(null);
      return;
    }

    setExpandedCell({ supplier, retailer });

    // Fetch details if not cached
    if (!detailsCache[key]) {
      try {
        setDetailsLoading(true);
        const res = await axios.post(`${API}/validation/csv-validation-details`, {
          csv_name: "sales_tracker",
          supplier,
          retailer: retailer.toUpperCase(),
        });
        if (res.data?.status === 1) {
          setDetailsCache((prev) => ({ ...prev, [key]: res.data.data }));
        }
      } catch (err) {
        console.error("Failed to fetch details", err);
      } finally {
        setDetailsLoading(false);
      }
    }
  };

  const formatVal = (v) => {
    if (v === null || v === undefined) return "-";
    return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const getMajorityValue = (mismatch) => {
    const vals = [mismatch.csv1, mismatch.csv2, mismatch.csv3, mismatch.csv4];
    const counts = {};
    vals.forEach((v) => {
      const k = String(v);
      counts[k] = (counts[k] || 0) + 1;
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return sorted[0]?.[0];
  };

  const renderCell = (row, retailer) => {
    const val = row[retailer];
    if (val === null || val === undefined) {
      return (
        <Td textAlign="center" px={2} py={2}>
          <FaMinus color="gray" size={14} />
        </Td>
      );
    }
    if (val === true) {
      return (
        <Td textAlign="center" px={2} py={2}>
          <FaCheckCircle color="#38A169" size={16} />
        </Td>
      );
    }
    // false — clickable
    const isExpanded = expandedCell?.supplier === row.supplier && expandedCell?.retailer === retailer;
    return (
      <Td
        textAlign="center"
        px={2}
        py={2}
        cursor="pointer"
        onClick={() => handleCellClick(row.supplier, retailer)}
        bg={isExpanded ? "red.50" : undefined}
        _hover={{ bg: "red.50" }}
      >
        <FaTimesCircle color="#E53E3E" size={16} />
      </Td>
    );
  };

  const renderDetailRow = (supplier) => {
    if (!expandedCell || expandedCell.supplier !== supplier) return null;

    const key = `${supplier}|${expandedCell.retailer}`;
    const details = detailsCache[key];

    return (
      <Tr>
        <Td colSpan={RETAILERS.length + 1} p={0} borderTop="2px solid #E53E3E">
          <Box bg="red.50" px={6} py={4}>
            <Flex align="center" mb={3}>
              <Badge colorScheme="red" mr={2}>{RETAILER_LABELS[expandedCell.retailer]}</Badge>
              <Text fontSize="sm" fontWeight="600" color="gray.700">
                {expandedCell.supplier} — Mismatched Metrics
              </Text>
            </Flex>
            {detailsLoading && !details ? (
              <Spinner size="sm" />
            ) : details && details.length > 0 ? (
              <Table size="sm" variant="simple" bg="white" borderRadius="md" overflow="hidden">
                <Thead>
                  <Tr bg="gray.100">
                    <Th fontSize="xs" py={2}>Metric</Th>
                    <Th fontSize="xs" py={2} isNumeric>Dashboard</Th>
                    <Th fontSize="xs" py={2} isNumeric>Store SOH</Th>
                    <Th fontSize="xs" py={2} isNumeric>Top Stores</Th>
                    <Th fontSize="xs" py={2} isNumeric>Top SKUs</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {details.map((m, i) => {
                    const majority = getMajorityValue(m);
                    return (
                      <Tr key={i}>
                        <Td fontSize="xs" py={1} fontWeight="500">{m.metric}</Td>
                        {["csv1", "csv2", "csv3", "csv4"].map((sheet) => {
                          const isOutlier = String(m[sheet]) !== majority;
                          return (
                            <Td
                              key={sheet}
                              fontSize="xs"
                              py={1}
                              isNumeric
                              color={isOutlier ? "red.600" : "gray.700"}
                              fontWeight={isOutlier ? "700" : "normal"}
                              bg={isOutlier ? "red.100" : undefined}
                            >
                              {formatVal(m[sheet])}
                            </Td>
                          );
                        })}
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            ) : (
              <Text fontSize="sm" color="gray.500">No detail data available</Text>
            )}
          </Box>
        </Td>
      </Tr>
    );
  };

  // Count summary stats
  const totalPass = rows.reduce((sum, r) => sum + RETAILERS.filter((ret) => r[ret] === true).length, 0);
  const totalFail = rows.reduce((sum, r) => sum + RETAILERS.filter((ret) => r[ret] === false).length, 0);

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="200px">
        <Spinner size="lg" />
      </Flex>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <Flex align="center" gap={4}>
          <Text fontSize="lg" fontWeight="600">Sales Tracker Validation</Text>
          <Badge colorScheme="green">{totalPass} Pass</Badge>
          <Badge colorScheme="red">{totalFail} Fail</Badge>
        </Flex>
        {updatedAt && (
          <Text fontSize="xs" color="gray.500">
            Last run: {new Date(updatedAt).toLocaleString()}
          </Text>
        )}
      </Flex>

      <Box overflowX="auto" borderRadius="md" border="1px solid" borderColor="gray.200">
        <Table size="sm" variant="simple">
          <Thead>
            <Tr bg="gray.50">
              <Th fontSize="xs" py={3} position="sticky" left={0} bg="gray.50" zIndex={1}>
                Supplier
              </Th>
              {RETAILERS.map((r) => (
                <Th key={r} textAlign="center" fontSize="xs" py={3} px={4}>
                  {RETAILER_LABELS[r]}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row) => (
              <React.Fragment key={row.supplier}>
                <Tr _hover={{ bg: "gray.50" }}>
                  <Td
                    fontSize="xs"
                    fontWeight="500"
                    py={2}
                    position="sticky"
                    left={0}
                    bg="white"
                    zIndex={1}
                    whiteSpace="nowrap"
                  >
                    {row.supplier}
                  </Td>
                  {RETAILERS.map((r) => renderCell(row, r))}
                </Tr>
                {renderDetailRow(row.supplier)}
              </React.Fragment>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default CsvValidator;
